/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

import IcalFreq from '../../ical/ical-freq';
import TargetEnum from '../../target-enum';
import TaskEnum from '../task-enum';

/**
 * Převaděč starých úloh na obsahové úlohy
 *
 * Při převodu se provádí vygenerování iCalendar plánovacího kalendáře,
 * pokud ho stará úloha nemá.
 * Kalendář se vygeneruje s jedinou událostí, která začíná 30 dní
 * před okamžikem generování a má délku 366 dní a opakuje se každý rok.
 * Měla by tak pokrýt všechny okamžiky v budoucnosti bez určení
 * koncového data.
 * Hodnota 30 dní byla zvolena, aby se předešlo problémům
 * s časovými zónami a byla tak jistota, že úloha bude vždy platná
 * v čase vytvoření, bez ohledu na změnu časové zóny, nebo mírnou
 * korekci času na zařízení.
 */
class TaskToContentTaskConverter {
  /**
   * @param {IcalApi} ICAL
   * @param {IcalTimeUtil} icalTimeUtil
   * @param {IcalUtil} icalUtil
   * @param {ContentTaskPlanUidGenerator} uidGenerator
   */
  constructor(ICAL, icalTimeUtil, icalUtil, uidGenerator) {
    this.ICAL = ICAL;
    /**
     * Počet dní od vytvoření úlohy, o kolik je posunuto její naplánování.
     * Při použití záporného čísla se začátek plánování posune do minulosti.
     * @private
     * @type {number}
     */
    this.adjustDays = -30;
    this.icalTimeUtil = icalTimeUtil;
    this.icalUtil = icalUtil;
    /**
     * Priorita hlavní obsahové úlohy,
     * která lze jako jediná převést i bez kalendáře.
     * @private
     * @type {number}
     */
    this.mainPriority = -1;
    /**
     * Cíl, který odpovídá hodnotě {@link TargetEnum.PRIMARY}.
     * @private
     * @type {string}
     */
    this.primaryTarget = 'display1.primary';
    this.uidGenerator = uidGenerator;
  }

  /**
   * Převede starou úlohu na obsahovou úlohu.
   * @param {Store} store
   * @param {Task} task
   * @param {Object} [opts={}]
   * @param {Date} [opts.nowDate]
   * @returns {ContentTask|undefined} Pokud nelze starou úlohu vytvořit,
   * vrací `undefined`.
   */
  convert(store, task, { nowDate = new Date() } = {}) {
    const { id, priority } = task;
    if (
      // Úloha musí být obsahová.
      task.taskEnum !== TaskEnum.CONTENT ||
      // Úloha musí mít jediný známý cíl, aby šel převést.
      task.targetEnum !== TargetEnum.PRIMARY ||
      // Úloha musí mít prioritu.
      isNil(priority)
    ) {
      return;
    }
    // Úloha musí mít kalendář.
    const nowIcalDate = this.ICAL.Time.fromJSDate(nowDate, true);
    const planIcal = this.convertPlanIcal(id, nowIcalDate, priority, {
      planIcal: task.planIcal
    });
    if (isNil(planIcal)) {
      return;
    }
    // Vytvoření obahové úlohy v požadovaném datovém skladu.
    return store.inject('contentTask', {
      id,
      layoutId: task.layoutId ?? undefined,
      mediumId: task.mediumId ?? undefined,
      planIcal,
      playlistId: task.playlistId ?? undefined,
      priority,
      target: this.primaryTarget
    });
  }

  /**
   * Vytvoří kalendář s plánem ze staré úlohy.
   * Pro to, aby neměla úloha žádný konec se vytváří úloha s ročním opakováním.
   * Roční opakování se používá, aby měla úloha co nejméně výskytů opakování,
   * protože jejich iterace má v iCal API špatný výkon.
   * Délka úlohy se nastvuje na 366 dní, což je maximální délka roku.
   * Tím se zaručí, že nebude existovat den, který by úloha nepokrývala a určení
   * konečného data nebude závislé na tom, jestli je aktuálně přestupný rok.
   * @param {DsId} id
   * @param {IcalApi.Time} nowDate
   * @param {number} priority
   * @param {Object} [opts={}]
   * @param {string} [opts.planIcal]
   * @private
   * @returns {string}
   */
  convertPlanIcal(id, nowDate, priority, { planIcal }) {
    if (!isNil(planIcal)) {
      return planIcal;
    }
    // Úloha musí mít kalendář, nebo musí mít prioritu hlavní obsahové úlohy,
    // jejíž kalendář lze vygenerovat.
    if (priority !== this.mainPriority) {
      return;
    }
    const { ICAL, icalTimeUtil, icalUtil } = this;
    const floatingZone = ICAL.Timezone.localTimezone;
    const floatingNowDate = nowDate.convertToZone(floatingZone);
    const startDate = icalTimeUtil.adjust(floatingNowDate, {
      days: this.adjustDays
    });
    const endDate = icalTimeUtil.adjust(startDate, { days: 366 });
    const vevent = icalUtil.createVEvent({
      endDate,
      recur: new ICAL.Recur({ freq: IcalFreq.Yearly }),
      stamp: nowDate,
      startDate,
      uid: this.uidGenerator.generate(id)
    });
    const vcalendar = icalUtil.createVCalendar({ subcomponents: [vevent] });
    return vcalendar.toString();
  }
}

export default TaskToContentTaskConverter;
